/**
 * Hook for managing conversation delete functionality.
 * Handles the delete mutation with confirmation dialog state and toast notifications.
 */

import { useDeleteAgentConversationMutation } from '@saaga/shared/store/api.store'
import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { orgRoutes } from '@/routes/org-routes.const'

export interface UseDeleteConversationReturn {
  deletingConversationId: string | null
  isDeleting: boolean
  startDeleting: (conversationId: string) => void
  cancelDeleting: () => void
  confirmDelete: () => void
}

export const useDeleteConversation = (): UseDeleteConversationReturn => {
  const [deletingConversationId, setDeletingConversationId] = useState<string | null>(null)
  const [deleteConversation, { isLoading: isDeleting }] = useDeleteAgentConversationMutation()
  const navigate = useNavigate()
  const { orgSlug, conversationId: activeConversationId } = useParams<{
    orgSlug: string
    conversationId?: string
  }>()

  const startDeleting = useCallback((conversationId: string): void => {
    setDeletingConversationId(conversationId)
  }, [])

  const cancelDeleting = useCallback((): void => {
    setDeletingConversationId(null)
  }, [])

  const confirmDelete = useCallback(async (): Promise<void> => {
    if (!deletingConversationId) return

    const isActiveConversation = activeConversationId === deletingConversationId

    try {
      await deleteConversation(deletingConversationId).unwrap()

      trackEvent('conversation_deleted', {})
      toast.success('Conversation deleted')
      setDeletingConversationId(null)

      if (isActiveConversation && orgSlug) {
        navigate(`/${orgSlug}/${orgRoutes.chat}`)
      }
    } catch {
      toast.error('Failed to delete conversation')
    }
  }, [deletingConversationId, deleteConversation, activeConversationId, orgSlug, navigate])

  return {
    deletingConversationId,
    isDeleting,
    startDeleting,
    cancelDeleting,
    confirmDelete,
  }
}
